import React, { useState, useRef, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Select from "../../../components/ui/Select";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import { ChevronDown, Check } from "lucide-react";

const FilterToolbar = ({
  filters,
  onFilterChange,
  onSearch,
  onClearFilters,
  resultCount,
  clusters = [],
  regions = [],
  teams = [],
  teamMembers = [],
}) => {
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [regionSearch, setRegionSearch] = useState("");
  const regionDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearch, setStatusSearch] = useState("");
  const statusDropdownRef = useRef(null);

  const clusterOptions = [
    { value: "all", label: "All Clusters" },
    ...(clusters?.map((cluster) => ({
      value: cluster.id,
      label: cluster.name,
    })) || []),
  ];

  // Filter regions based on selected cluster (regions are now tied to projects, but for now keep cluster filter)
  const filteredRegions = regions || [];

  const regionOptions =
    filteredRegions?.map((region) => ({
      value: region.id,
      label: region.name,
    })) || [];

  const selectedRegionIds = Array.isArray(filters?.region)
    ? filters.region
    : [];
  const filteredRegionOptions = regionSearch.trim()
    ? regionOptions.filter((opt) =>
        opt.label.toLowerCase().includes(regionSearch.toLowerCase()),
      )
    : regionOptions;

  useEffect(() => {
    if (!regionDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        regionDropdownRef.current &&
        !regionDropdownRef.current.contains(e.target)
      ) {
        setRegionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [regionDropdownOpen]);

  useEffect(() => {
    if (!statusDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(e.target)
      ) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [statusDropdownOpen]);

  const toggleRegion = (regionId) => {
    const next = selectedRegionIds.includes(regionId)
      ? selectedRegionIds.filter((id) => id !== regionId)
      : [...selectedRegionIds, regionId];
    onFilterChange({ ...filters, region: next });
  };

  const regionDisplayLabel =
    selectedRegionIds.length === 0
      ? "All Regions"
      : selectedRegionIds.length === 1
        ? (regionOptions.find((o) => o.value === selectedRegionIds[0])?.label ??
          "1 selected")
        : `${selectedRegionIds.length} regions selected`;

  const teamOptions = [
    { value: "all", label: "All Teams" },
    { value: "not_assigned", label: "Not Assigned" },
    ...(teams?.map((team) => ({
      value: team.id,
      label: team.name,
    })) || []),
  ];

  const teamMemberOptions = [
    { value: "all", label: "All Team Members" },
    { value: "not_assigned", label: "Not Assigned" },
    ...(teamMembers?.map((member) => ({
      value: member.id,
      label: member.name,
    })) || []),
  ];

  const statusOptions = [
    { value: "survey_not_started", label: "Survey Not Started" },
    { value: "survey_inprogress", label: "Survey In Progress" },
    { value: "survey_complete", label: "Survey Complete" },
    { value: "survey_unworkable", label: "Survey Unworkable" },
    { value: "transport_pending", label: "Transport Pending" },
    { value: "transport_inprogress", label: "Transport In Progress" },
    { value: "transport_complete", label: "Transport Complete" },
    { value: "civil_pending", label: "Civil Pending" },
    { value: "civil_inprogress", label: "Civil In Progress" },
    { value: "civil_complete", label: "Civil Complete" },
    { value: "technical_pending", label: "Technical Pending" },
    { value: "technical_inprogress", label: "Technical In Progress" },
    { value: "technical_complete", label: "Technical Complete" },
    {
      value: "commissioning_not_started",
      label: "Commissioning Not Started",
    },
    {
      value: "commissioning_inprogress",
      label: "Commissioning In Progress",
    },
    { value: "commissioning_complete", label: "Commissioning Complete" },
  ];

  const selectedStatusValues = Array.isArray(filters?.status)
    ? filters.status
    : filters?.status && filters.status !== "all"
      ? [filters.status]
      : [];

  const filteredStatusOptions = statusSearch.trim()
    ? statusOptions.filter((opt) =>
        opt.label.toLowerCase().includes(statusSearch.toLowerCase()),
      )
    : statusOptions;

  const toggleStatus = (statusValue) => {
    const next = selectedStatusValues.includes(statusValue)
      ? selectedStatusValues.filter((s) => s !== statusValue)
      : [...selectedStatusValues, statusValue];
    onFilterChange({ ...filters, status: next });
  };

  const statusDisplayLabel =
    selectedStatusValues.length === 0
      ? "All Statuses"
      : selectedStatusValues.length === 1
        ? (statusOptions.find((o) => o.value === selectedStatusValues[0])
            ?.label ?? "1 selected")
        : `${selectedStatusValues.length} statuses selected`;

  const clearStatusSelection = () => {
    onFilterChange({ ...filters, status: [] });
  };

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    } else {
      onFilterChange({
        search: "",
        searchType: "general",
        bulkServiceNumbers: "",
        cluster: "all",
        region: [],
        status: [],
        team: "all",
        teamMember: "all",
        quickFilter: "all",
        surveyCompletedOn: "",
      });
    }
  };

  const handleQuickFilter = (quickFilter) => {
    // If clicking the same filter, deselect it
    const newQuickFilter =
      filters?.quickFilter === quickFilter ? "all" : quickFilter;
    onFilterChange({ ...filters, quickFilter: newQuickFilter });
  };

  const hasActiveFilters =
    filters?.search ||
    filters?.bulkServiceNumbers ||
    filters?.searchType !== "general" ||
    filters?.cluster !== "all" ||
    (Array.isArray(filters?.region) && filters.region.length > 0) ||
    (Array.isArray(filters?.status) && filters.status.length > 0) ||
    (typeof filters?.status === "string" &&
      filters.status !== "all" &&
      filters.status !== "") ||
    filters?.team !== "all" ||
    filters?.teamMember !== "all" ||
    filters?.quickFilter !== "all" ||
    filters?.surveyCompletedOn;
  const selectedCompletionQuickFilterPhase =
    filters?.quickFilter === "completed_surveys"
      ? "Survey"
      : filters?.quickFilter === "completed_civil"
        ? "Civil"
        : filters?.quickFilter === "completed_technical"
          ? "Technical"
          : filters?.quickFilter === "completed_commissioning"
            ? "Commissioning"
            : "";

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 space-y-4">
      {/* Search Type Toggle */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          Search Type:
        </span>
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            type="button"
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              filters?.searchType === "general"
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground hover:bg-muted"
            }`}
            onClick={() =>
              onFilterChange({ ...filters, searchType: "general" })
            }
          >
            General Search
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              filters?.searchType === "bulk_usn"
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground hover:bg-muted"
            }`}
            onClick={() =>
              onFilterChange({ ...filters, searchType: "bulk_usn" })
            }
          >
            Bulk CIN/USN Search
          </button>
        </div>
      </div>

      {/* Search and Result Count */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {filters?.searchType === "general" ? (
          <div className="w-full sm:w-auto flex-1 max-w-md flex gap-2 items-center">
            <Input
              type="search"
              placeholder="Search by name, Aadhaar, phone..."
              value={filters?.search}
              onChange={(e) =>
                onFilterChange({ ...filters, search: e?.target?.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearch?.();
              }}
              className="w-full"
            />
            <Button
              variant="default"
              size="default"
              onClick={() => onSearch?.()}
              className="shrink-0"
            >
              Search
            </Button>
          </div>
        ) : (
          <div className="w-full flex-1 flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <textarea
                placeholder="Enter comma-separated CINs or USNs (e.g., 20260000001, S123456, 20260000002)"
                value={filters?.bulkServiceNumbers}
                onChange={(e) =>
                  onFilterChange({
                    ...filters,
                    bulkServiceNumbers: e?.target?.value,
                  })
                }
                className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter CINs (Customer ID) or USNs (Service Numbers) separated by
                commas. Searches both fields.
              </p>
            </div>
            <Button
              variant="default"
              size="default"
              onClick={() => onSearch?.()}
              className="shrink-0 self-start sm:self-end"
            >
              Search
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Users" size={16} />
          <span className="font-medium">{resultCount} customers</span>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground mr-2">
          Quick Filters:
        </span>
        <Button
          variant={
            filters?.quickFilter === "completed_surveys" ? "default" : "outline"
          }
          size="sm"
          onClick={() => handleQuickFilter("completed_surveys")}
          className="text-xs sm:text-sm"
        >
          Completed Surveys
        </Button>
        <Button
          variant={
            filters?.quickFilter === "completed_civil" ? "default" : "outline"
          }
          size="sm"
          onClick={() => handleQuickFilter("completed_civil")}
          className="text-xs sm:text-sm"
        >
          Completed Civil
        </Button>
        <Button
          variant={
            filters?.quickFilter === "completed_technical"
              ? "default"
              : "outline"
          }
          size="sm"
          onClick={() => handleQuickFilter("completed_technical")}
          className="text-xs sm:text-sm"
        >
          Completed Technical
        </Button>
        <Button
          variant={
            filters?.quickFilter === "completed_commissioning"
              ? "default"
              : "outline"
          }
          size="sm"
          onClick={() => handleQuickFilter("completed_commissioning")}
          className="text-xs sm:text-sm"
        >
          Completed Commissioning
        </Button>
        <div className="flex items-center gap-2 ml-0 sm:ml-2">
          <label className="text-xs sm:text-sm font-medium text-foreground whitespace-nowrap">
            Phase completed
          </label>
          <input
            type="datetime-local"
            className="flex h-8 rounded-md border border-input bg-background px-2 py-1 text-xs sm:text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
            value={filters?.surveyCompletedOn || ""}
            disabled={!selectedCompletionQuickFilterPhase}
            onChange={(e) =>
              onFilterChange({
                ...filters,
                surveyCompletedOn: e.target.value || "",
              })
            }
          />
        </div>
      </div>
      {/* Filter Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="flex flex-col gap-1 relative" ref={regionDropdownRef}>
          <label className="text-sm font-medium text-foreground">Region</label>
          <button
            type="button"
            onClick={() => {
              setRegionDropdownOpen((o) => !o);
              if (!regionDropdownOpen) setRegionSearch("");
            }}
            className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${selectedRegionIds.length === 0 ? "text-muted-foreground" : "text-black dark:text-white"}`}
          >
            <span className="truncate text-left">{regionDisplayLabel}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${regionDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>
          {regionDropdownOpen && (
            <div className="absolute left-0 bottom-full z-[10000] mb-1 w-full min-w-[200px] max-h-[280px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
              <div className="p-2 border-b border-border">
                <input
                  type="search"
                  placeholder="Search regions..."
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  autoFocus
                />
              </div>
              <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[220px]">
                {filteredRegionOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No regions match
                  </div>
                ) : (
                  filteredRegionOptions.map((opt) => {
                    const isSelected = selectedRegionIds.includes(opt.value);
                    return (
                      <div
                        key={opt.value}
                        role="option"
                        aria-selected={isSelected}
                        className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                        onClick={() => toggleRegion(opt.value)}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            isSelected
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-input"
                          }`}
                        >
                          {isSelected ? <Check className="h-3 w-3" /> : null}
                        </span>
                        <span className="truncate">{opt.label}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        <Select
          label="Cluster"
          options={clusterOptions}
          value={filters?.cluster}
          onChange={(value) => {
            // Reset site filter when cluster changes
            onFilterChange({ ...filters, cluster: value, site: "all" });
          }}
        />

        <div className="flex flex-col gap-1 relative" ref={statusDropdownRef}>
          <label className="text-sm font-medium text-foreground">Status</label>
          <button
            type="button"
            onClick={() => {
              setStatusDropdownOpen((o) => !o);
              if (!statusDropdownOpen) setStatusSearch("");
            }}
            className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${selectedStatusValues.length === 0 ? "text-muted-foreground" : "text-black dark:text-white"}`}
          >
            <span className="truncate text-left">{statusDisplayLabel}</span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${statusDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>
          {statusDropdownOpen && (
            <div className="absolute left-0 bottom-full z-[10000] mb-1 w-full min-w-[260px] max-h-[320px] bg-white dark:bg-gray-800 text-black dark:text-white border border-border rounded-md shadow-lg overflow-hidden flex flex-col">
              <div className="p-2 border-b border-border">
                <input
                  type="search"
                  placeholder="Search statuses..."
                  value={statusSearch}
                  onChange={(e) => setStatusSearch(e.target.value)}
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  autoFocus
                />
              </div>
              <div className="py-1 overflow-y-auto flex-1 min-h-0 max-h-[240px]">
                {filteredStatusOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No statuses match
                  </div>
                ) : (
                  <>
                    <div
                      role="option"
                      aria-selected={selectedStatusValues.length === 0}
                      className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground border-b border-border/70"
                      onClick={clearStatusSelection}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selectedStatusValues.length === 0
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-input"
                        }`}
                      >
                        {selectedStatusValues.length === 0 ? (
                          <Check className="h-3 w-3" />
                        ) : null}
                      </span>
                      <span className="truncate font-medium">All Statuses</span>
                    </div>
                    {filteredStatusOptions.map((opt) => {
                      const isSelected = selectedStatusValues.includes(opt.value);
                      return (
                        <div
                          key={opt.value}
                          role="option"
                          aria-selected={isSelected}
                          className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                          onClick={() => toggleStatus(opt.value)}
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              isSelected
                                ? "bg-primary border-primary text-primary-foreground"
                                : "border-input"
                            }`}
                          >
                            {isSelected ? <Check className="h-3 w-3" /> : null}
                          </span>
                          <span className="truncate">{opt.label}</span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <Select
          label="Team"
          options={teamOptions}
          value={filters?.team}
          onChange={(value) => onFilterChange({ ...filters, team: value })}
        />

        <Select
          label="Team Member"
          options={teamMemberOptions}
          value={filters?.teamMember}
          onChange={(value) =>
            onFilterChange({ ...filters, teamMember: value })
          }
        />

      </div>
      {/* Clear Filters */}
      {hasActiveFilters && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            iconPosition="left"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterToolbar;
